"use client";

import { CSSProperties, useEffect, useRef } from "react";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";

/**
 * Static pixelated photo: the image is drawn into a cols × rows canvas and scaled up with
 * `image-rendering: pixelated`, which reproduces Figma's pre-pixelated trader layers from the CMS photo.
 * `block` is the block edge in Figma px; `w`/`h` the photo box in Figma px (rendered in rem, px / 8).
 */
export default function QePixelPhoto({ src, w, h, block, className, style }: { src?: string; w: number; h: number; block: number; className?: string; style?: CSSProperties }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = ref.current;
    const clean = src ? stegaClean(src) : "";
    if (!cv || !clean) return;
    const cols = Math.ceil(w / block), rows = Math.ceil(h / block);
    cv.width = cols;
    cv.height = rows;
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      const g = cv.getContext("2d");
      if (!g) return;
      g.imageSmoothingQuality = "high";
      g.clearRect(0, 0, cols, rows);
      g.drawImage(im, 0, 0, cols, rows);
    };
    im.src = clean.includes("cdn.sanity.io") ? `${clean}${clean.includes("?") ? "&" : "?"}w=${Math.ceil(w * 2)}` : clean;
  }, [src, w, h, block]);
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={cn("pointer-events-none absolute block [image-rendering:pixelated]", className)}
      style={{ width: `${w / 8}rem`, height: `${(Math.ceil(h / block) * block) / 8}rem`, ...style }}
    />
  );
}
